import { error } from 'cc';
import Network, { RequestSetting, ResponseDataType } from './Network';
import ErrorName from '../../ErrorHandler/ErrorName';
import Cryptor from '../../Toolkit/Helper/Cryptor';
import Authorizer from '../../Toolkit/Helper/Authorizer';
import Delay from '../../Toolkit/Utils/Delay';
import Game from '../../Game';
import { joinURLParam } from '../../Toolkit/Utils/Utility';

export enum HttpMethod {
    GET = 'GET',
    POST = 'POST',
    PUT = 'PUT',
    PATCH = 'PATCH',
    DELETE = 'DELETE',
}

export default class NetworkHttp extends Network {
    constructor(
        url: string,
        cryptor: Cryptor,
        private _authorizer: Authorizer,
        private _routeMap: {
            [key: string]: {
                method: HttpMethod;
                path: string;
            };
        },
        private _needEncrypt: boolean = Game.config.needEncrypt,
    ) {
        super(url, cryptor);
    }

    public open(): Promise<void> {
        return Promise.resolve();
    }

    public reconnect(): Promise<void> {
        throw new Error('not allowed reconnect manually');
    }

    public async send(path: string, postData: any, setting: RequestSetting): Promise<any> {
        const { expectedErrors, handleAllErrors, timeout } = setting;

        try {
            if (timeout < 0 && timeout !== -1) {
                throw new Error('Invalid timeout');
            }
            const { url, ...request } = this._buildRequest(this.url, path, postData);
            const response = await Promise.race([
                fetch(url, request),
                timeout >= 0 ? Delay.reject<Response>(timeout, ErrorName.TIMEOUT, true) : Delay.forever(),
            ]);
            const rawData = await response.json();
            const { Data, Err, ErrorCode, Log, Message }: ResponseDataType = typeof rawData === 'string' ? JSON.parse(this.cryptor.decrypt(rawData)) : rawData;
            const errorKey = Err ?? ErrorCode?.toString();
            const errorMessage = Log ?? Message;
            if (errorMessage) {
                error('Error log:', errorMessage);
            }
            if (errorKey) {
                throw errorKey;
            }
            // boardcast
            if (this.hasEventListener(path)) {
                this.emit(path, Data);
            }
            return Data;
        } catch (reason) {
            if (handleAllErrors || expectedErrors.includes(reason)) {
                throw reason;
            }
            if (reason === 'Invalid timeout') {
                error(`Invalid timeout at path ${path as string}`);
            } else if (reason === ErrorName.TIMEOUT) {
                error(`timeout at path ${path as string}`);
                this.emit(Network.EVENT_TYPE.ERROR, ErrorName.TIMEOUT, '', { path, form: postData });
            } else if (this.hasEventListener(`${path}_error`)) {
                // server send error initiatively, so no reject function
                this.emit(`${path}_error`, reason);
            } else if (reason instanceof Error && reason.message === 'Failed to fetch') {
                // handle network error, ex: no internet connection
                if (expectedErrors.includes('Failed to fetch')) {
                    throw reason;
                }
                this.emit(Network.EVENT_TYPE.ERROR, ErrorName.CONNECTION_FAILED, '', { path, form: postData });
            } else {
                this.emit(Network.EVENT_TYPE.ERROR, reason, '', { path, form: postData });
            }
            return Delay.forever();
        }
    }

    public close(): void {}

    private _buildRequest(url: string, path: string, postData: any): RequestInit & { url: string } {
        const headers: Headers = new Headers();
        const route = this._routeMap[path] ?? { method: HttpMethod.GET, path };
        url += route.path;
        headers.append('Content-Type', 'application/json');
        if (this._authorizer) {
            this._authorizer.authorize(headers);
        }

        if (!postData) {
            return { url, method: route.method, headers };
        }

        // url中有{xx} 則會抓取postData[xx]進行替換
        let pairs: [string, string][] = Object.entries(postData);
        const matches = route.path.match(/\{[^}]+\}/gi);
        if (matches) {
            matches.forEach((match) => {
                const mathchKey = match.slice(1, -1);
                if (postData[mathchKey]) {
                    url = url.replace(match, postData[mathchKey]);
                    pairs = pairs.filter(([key]) => key !== mathchKey);
                }
            });
        }

        if (route.method === HttpMethod.GET) {
            url = joinURLParam(url, pairs);
            return { url, method: route.method, headers };
        }

        const body = JSON.stringify(this._needEncrypt ? this.cryptor.encrypt(JSON.stringify(postData)) : postData);
        return { url, method: route.method, headers, body };
    }
}
